import SectorHero from "@/components/SectorHero";
import StatsBand from "@/components/StatsBand";
import PartnersGrid from "@/components/PartnersGrid";
import TestimonialSlider from "@/components/TestimonialSlider";
import ContactSection from "@/components/ContactSection";
import { Award, Users, Globe, Sparkles, ShieldCheck, Heart } from "lucide-react";

const values = [
  { icon: Award, title: "Excellence technique", desc: "Notre bureau d'études interne, formé en continu, garantit des dimensionnements rigoureux et des économies vérifiées." },
  { icon: Users, title: "Engagement long terme", desc: "Un chargé d'affaires dédié vous accompagne pendant toute la durée de vie du projet — pas juste jusqu'au paiement." },
  { icon: Globe, title: "Présence nationale", desc: "5 implantations en France métropolitaine, intervention DOM-TOM, équipes terrain à moins de 200 km de votre site." },
  { icon: Sparkles, title: "Innovation continue", desc: "Veille réglementaire, dialogue permanent avec l'ADEME et le PNCEE, anticipation des évolutions des fiches CEE." },
  { icon: ShieldCheck, title: "Sécurité juridique", desc: "0 % de redressement PNCEE depuis notre création. Toutes nos opérations passent l'audit de contrôle." },
  { icon: Heart, title: "Mission climatique", desc: "Chaque euro de CEE valorisé évite l'émission de plusieurs kilos de CO₂. Notre raison d'être va bien au-delà du financement." },
];

const milestones = [
  { year: "2023", text: "Fondation d'ECONATION à Paris autour d'un noyau ingénieur + énergéticien avec l'ambition de simplifier l'accès au dispositif CEE." },
  { year: "2024", text: "Obtention de la délégation CEE auprès du PNCEE. Premiers programmes multi-sites pour des industriels agroalimentaires." },
  { year: "2025", text: "Ouverture des bureaux Lyon, Marseille, Toulouse et Lille. Création du bureau d'études interne et cap des 100 projets accompagnés." },
];

export default function APropos() {
  return (
    <div data-testid="apropos-page">
      <SectorHero
        kicker="À propos"
        title="Une mission claire : décarboner l'économie française."
        blurb="ECONATION rassemble ingénieurs, énergéticiens, juristes CEE et chefs de projet autour d'une obsession : transformer chaque euro de Certificat d'Économies d'Énergie en kilogrammes de CO₂ évités."
        image="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
        tags={["Fondée en 2023", "Délégataire CEE", "ISO 50001", "5 bureaux", "62 collaborateurs"]}
      />

      {/* VALUES */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32">
          <div className="max-w-3xl mb-14">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Nos valeurs</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight text-balance leading-[1.05]">
              Six principes qui guident chaque mission ECONATION.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v) => (
              <article key={v.title} data-testid={`value-${v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-white border border-black/[0.06] rounded-2xl p-7 hover:border-[#10B981]/40 hover:shadow-[0_24px_60px_-30px_rgba(16,185,129,0.3)] transition-all">
                <div className="h-12 w-12 rounded-xl bg-[#064E3B] grid place-items-center text-[#10B981] mb-5">
                  <v.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-2">{v.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{v.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-[#F8FAFC] border-y border-black/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32">
          <div className="max-w-3xl mb-14">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Notre histoire</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight text-balance leading-[1.05]">
              Une jeune entreprise, une trajectoire fulgurante.
            </h2>
          </div>
          <ol className="relative border-l-2 border-[#10B981]/20 pl-8 space-y-10">
            {milestones.map((m, i) => (
              <li key={m.year} data-testid={`milestone-${m.year}`} className="relative">
                <span className="absolute -left-[42px] top-1 h-4 w-4 rounded-full bg-white border-2 border-[#10B981]" />
                <div className="font-display text-2xl md:text-3xl font-bold text-[#10B981]">{m.year}</div>
                <p className="text-[#475569] mt-2 max-w-2xl leading-relaxed">{m.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <StatsBand dark />
      <PartnersGrid />
      <TestimonialSlider />
      <ContactSection />
    </div>
  );
}
